import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { playerModalRefreshKeyAtom, setPlayerModalUrlParam, usePlayerModalStateValue } from "@/hooks/playerModal";
import { InfoIcon, ListIcon, HistoryIcon, GavelIcon, LockIcon, ShieldAlertIcon } from "lucide-react";
import PlayerInfoTab from "./PlayerInfoTab";
import { useEffect, useMemo, useState } from "react";
import PlayerIdsTab from "./PlayerIdsTab";
import PlayerFlagsTab from "./PlayerFlagsTab";
import PlayerHistoryTab from "./PlayerHistoryTab";
import PlayerBanTab from "./PlayerBanTab";
import PlayerJailTab from "./PlayerJailTab";
import GenericSpinner from "@/components/GenericSpinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { playerlistNameModeAtom } from "@/hooks/playerlist";
import { actionModalOpenAtom } from "@/hooks/actionModal";
import { useAtomValue } from "jotai";
import { cn } from "@/lib/utils";
import { useBackendApi } from "@/hooks/fetch";
import { PlayerModalResp, PlayerModalSuccess } from "@shared/playerApiTypes";
import PlayerModalFooter from "./PlayerModalFooter";
import { ModalContent, ModalTabMessage, ModalTabsList, ModalTabWrapper, type ModalTabInfo } from "@/components/modal-tabs";


//only shown when the connect check flagged the player, right after the IDs tab
const flagsTab: ModalTabInfo = {
    title: 'Flags',
    icon: <ShieldAlertIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    className: 'hover:border-warning/45 hover:bg-warning/10 hover:text-warning',
};

const baseModalTabs: ModalTabInfo[] = [
    {
        title: 'Info',
        icon: <InfoIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    },
    {
        title: 'History',
        icon: <HistoryIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    },
    {
        title: 'IDs',
        icon: <ListIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    },
    //GMW: the Admin Jail tab is hidden because this core has no jail backend.
    //PlayerJailTab and its render below are left in place - putting this entry
    //back is all it takes to restore the tab once the backend exists:
    // { title: 'Admin Jail', icon: <LockIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    //   className: 'hover:border-jail/45 hover:bg-jail/10 hover:text-jail' },
    {
        title: 'Ban',
        icon: <GavelIcon className="mr-2 h-5 w-5 hidden xs:block" />,
        className: 'hover:border-danger/45 hover:bg-danger/10 hover:text-danger-soft',
    }
];


export default function PlayerModal() {
    const nameMode = useAtomValue(playerlistNameModeAtom);
    const isActionModalOpen = useAtomValue(actionModalOpenAtom);
    const { isModalOpen, closeModal, playerRef } = usePlayerModalStateValue();
    const [selectedTab, setSelectedTab] = useState(baseModalTabs[0].title);
    const [currRefreshKey, setCurrRefreshKey] = useState(0);
    const externalRefreshKey = useAtomValue(playerModalRefreshKeyAtom);
    const [modalData, setModalData] = useState<PlayerModalSuccess | undefined>(undefined);
    const [modalError, setModalError] = useState('');
    const [tsFetch, setTsFetch] = useState(0);
    const playerQueryApi = useBackendApi<PlayerModalResp>({
        method: 'GET',
        path: `/player`,
        abortOnUnmount: true,
    });

    //Helper for tabs to be able to refresh the modal data
    const refreshModalData = () => {
        setCurrRefreshKey(currRefreshKey + 1);
    };

    //Querying player data when reference is available
    useEffect(() => {
        if (!playerRef) return;
        setModalData(undefined);
        setModalError('');
        playerQueryApi({
            queryParams: playerRef,
            success: (resp) => {
                if ('error' in resp) {
                    setModalError(resp.error);
                } else {
                    setModalData(resp);
                    setTsFetch(Math.round(Date.now() / 1000));
                    //Set the ref param now that we know the player: discord id, license as fallback
                    const discordId = [...resp.player.idsOnline, ...resp.player.idsOffline]
                        .find((id) => id.startsWith('discord:'));
                    if (discordId) {
                        setPlayerModalUrlParam(discordId.slice('discord:'.length));
                    } else if (resp.player.license) {
                        setPlayerModalUrlParam(resp.player.license);
                    }
                }
            },
            error: (error) => {
                setModalError(error);
            },
        });
    }, [playerRef, currRefreshKey, externalRefreshKey]);

    const isFlagged = !!modalData?.player.ipFlags?.flags.length;
    const modalTabs = useMemo(() => {
        if (!isFlagged) return baseModalTabs;
        const idsIndex = baseModalTabs.findIndex((tab) => tab.title === 'IDs');
        return baseModalTabs.toSpliced(idsIndex + 1, 0, flagsTab);
    }, [isFlagged]);

    //Resetting selected tab when modal is closed
    useEffect(() => {
        if (!isModalOpen) {
            setTimeout(() => {
                setSelectedTab(baseModalTabs[0].title);
            }, 200);
        }
    }, [isModalOpen]);

    //the flags tab is gone when switching to a player that was not flagged
    useEffect(() => {
        if (!isFlagged && selectedTab === flagsTab.title) {
            setSelectedTab(baseModalTabs[0].title);
        }
    }, [isFlagged, selectedTab]);

    //the action modal covers this one, so it can be open but not visible
    const isModalVisible = isModalOpen && !isActionModalOpen;
    const handleOpenClose = (newOpenState: boolean) => {
        if (isModalVisible && !newOpenState) {
            closeModal();
        }
    };

    //Move to tab up or down
    const handleTabButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const currentIndex = modalTabs.findIndex((tab) => tab.title === selectedTab);
            const nextIndex = e.key === 'ArrowUp' ? currentIndex - 1 : currentIndex + 1;
            const nextTab = modalTabs[nextIndex];
            if (nextTab) {
                setSelectedTab(nextTab.title);
                const nextButton = document.getElementById(`player-modal-tab-${nextTab.title}`);
                if (nextButton) {
                    nextButton.focus();
                }
            }
        }
    }

    let pageTitle: JSX.Element;
    if (modalData) {
        //the title follows the name mode of the playerlist, hovering it shows all of them
        const rpName = modalData.player.coreData?.name ?? modalData.player.coreName;
        const jobBase = modalData.player.coreData?.job ?? modalData.player.coreJob;
        const jobGrade = modalData.player.coreData?.jobGrade ?? modalData.player.coreJobGrade;
        const job = jobBase && jobGrade ? `${jobBase} - ${jobGrade}` : jobBase;
        const group = modalData.player.coreData?.group ?? modalData.player.coreGroup;
        const shownName = nameMode === 'rp'
            ? rpName ?? modalData.player.displayName
            : modalData.player.displayName;
        const nameNode = (rpName || group || job) ? (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">{shownName}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-left">
                    <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-xs">
                        <dt className="text-muted-foreground">FiveM</dt>
                        <dd>{modalData.player.displayName}</dd>
                        {rpName ? (<>
                            <dt className="text-muted-foreground">RP</dt>
                            <dd>{rpName}</dd>
                        </>) : null}
                        {group ? (<>
                            <dt className="text-muted-foreground">Group</dt>
                            <dd>{group}</dd>
                        </>) : null}
                        {job ? (<>
                            <dt className="text-muted-foreground">Job</dt>
                            <dd>{job}</dd>
                        </>) : null}
                    </dl>
                </TooltipContent>
            </Tooltip>
        ) : <span>{shownName}</span>;
        if (modalData.player.netid) {
            pageTitle = <>
                <span className="text-success-inline font-mono mr-2">[{modalData.player.netid}]</span>
                {nameNode}
            </>;
        } else {
            pageTitle = <>
                <span className="text-destructive-inline font-mono mr-2">[OFF]</span>
                {nameNode}
            </>;
        }
    } else if (modalError) {
        pageTitle = <span className="text-destructive-inline">Error</span>;
    } else {
        pageTitle = <span className="text-muted-foreground italic">Loading...</span>;
    }

    return (
        <Dialog open={isModalVisible} onOpenChange={handleOpenClose}>
            <DialogContent
                className="max-w-2xl h-full sm:h-auto max-h-full p-0 gap-1 sm:gap-4 flex flex-col"
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    (event.currentTarget as HTMLElement | null)?.focus();
                }}
            >
                <DialogHeader className="px-4 py-3 border-b">
                    <DialogTitle className="line-clamp-1 leading-7 break-all mr-6">
                        {pageTitle}
                    </DialogTitle>
                </DialogHeader>

                <ModalContent>
                    <ModalTabsList>
                        {modalTabs.map((tab) => (
                            <Button
                                id={`player-modal-tab-${tab.title}`}
                                key={tab.title}
                                variant={selectedTab === tab.title ? "secondary" : "ghost"}
                                className={cn(
                                    'w-full justify-center md:justify-start',
                                    'h-9 px-2 text-base',
                                    tab.className,
                                )}
                                onClick={() => setSelectedTab(tab.title)}
                                onKeyDown={handleTabButtonKeyDown}
                            >
                                {tab.icon}{tab.title}
                            </Button>
                        ))}
                    </ModalTabsList>
                    
                    {/* fixed body height like the ingame modal, so a long tab scrolls instead of growing */}
                    {/* NOTE: the tab buttons need 196px, the info tab ~250px and the ban form ~236px */}
                    <ModalTabWrapper className="md:h-[17rem] md:min-h-0 md:max-h-none md:pt-1">
                        {!modalData ? (
                            <ModalTabMessage>
                                {modalError ? (
                                    <span className="text-destructive-inline">Error: {modalError}</span>
                                ) : (
                                    <GenericSpinner msg="Loading..." />
                                )}
                            </ModalTabMessage>
                        ) : (
                            <>
                                {selectedTab === 'Info' && <PlayerInfoTab
                                    player={modalData.player}
                                    playerRef={playerRef!}
                                    serverTime={modalData.serverTime}
                                    tsFetch={tsFetch}
                                    setSelectedTab={setSelectedTab}
                                    refreshModalData={refreshModalData}
                                />}
                                {selectedTab === 'History' && <PlayerHistoryTab
                                    actionHistory={modalData.player.actionHistory}
                                    serverTime={modalData.serverTime}
                                    refreshModalData={refreshModalData}
                                />}
                                {selectedTab === 'IDs' && <PlayerIdsTab
                                    player={modalData.player}
                                    playerRef={playerRef!}
                                    refreshModalData={refreshModalData}
                                />}
                                {selectedTab === 'Flags' && <PlayerFlagsTab
                                    player={modalData.player}
                                />}
                                {selectedTab === 'Admin Jail' && <PlayerJailTab
                                    jailTemplates={modalData.jailTemplates}
                                    playerRef={playerRef!}
                                    player={modalData.player}
                                    serverTime={modalData.serverTime}
                                />}
                                {selectedTab === 'Ban' && <PlayerBanTab
                                    banTemplates={modalData.banTemplates}
                                    playerRef={playerRef!}
                                    player={modalData.player}
                                    serverTime={modalData.serverTime}
                                />}
                            </>
                        )}
                    </ModalTabWrapper>
                </ModalContent>
                <PlayerModalFooter
                    playerRef={playerRef!}
                    player={modalData?.player}
                    warnTemplates={modalData?.warnTemplates}
                />
            </DialogContent>
        </Dialog>
    );
}
